import { z } from "zod";
import { LIMITS, optional } from "@shared/config";

// ── вход и регистрация ───────────────────────────────────────────────

/**
 * Одна форма на два режима.
 *
 * Правила у них разные, и разница не в наборе полей, а в требованиях: при
 * входе пароль — «что набрали, то и проверим», при регистрации он от восьми
 * знаков, и имя обязательно. Записать это набором необязательных полей значит
 * записать два правила одним, то есть ни одного, — поэтому режим входит в
 * форму полем, а оговорка про регистрацию стоит отдельно и видна.
 *
 * Чего здесь нет: формата имени и годности кода. Первое знает доменный
 * `Username`, второе — `InviteCode`; `register.rs` отдельно оговаривает, что
 * второго описания заводить не надо.
 */
export const authSchema = z
  .object({
    mode: z.enum(["login", "register"]),
    /*
     * Два сообщения, а не одно: пустое поле — это не опечатка, и говорить
     * человеку «похоже на опечатку» там, где он вообще ничего не набрал,
     * значит отвечать не на его вопрос. Соседний пароль различает это с
     * самого начала, а почта — нет.
     */
    email: z.string().min(1, "Введите почту").pipe(z.email("Похоже на опечатку в адресе")),
    password: z.string().min(1, "Введите пароль"),
    username: z.string(),
    displayName: optional(LIMITS.registerName.max),
    invite: z.string(),
  })
  .superRefine((value, context) => {
    if (value.mode !== "register") return;

    if (value.password.length < LIMITS.password.min) {
      context.addIssue({
        code: "custom",
        path: ["password"],
        message: `От ${LIMITS.password.min} знаков`,
      });
    }

    if (value.password.length > LIMITS.password.max) {
      context.addIssue({
        code: "custom",
        path: ["password"],
        message: `Не длиннее ${LIMITS.password.max} знаков`,
      });
    }

    if (!value.username.trim()) {
      context.addIssue({ code: "custom", path: ["username"], message: "Как вас звать?" });
    }
  });

export type AuthForm = z.infer<typeof authSchema>;
